/**
 * useToast — sistema de toasts sin dependencias externas.
 *
 * Uso:
 *   import { toast } from './useToast';
 *   toast.success("Segmento duplicado");
 *   toast.error("Error al borrar");
 *
 * El componente <Toaster /> se monta una sola vez en App.tsx, fuera del layout, para que también
 * se vean los toasts de /login. Una segunda instancia mostraría cada toast dos veces.
 */

import { useState, useEffect, useCallback } from "react";

export interface ToastItem {
    id: number;
    message: string;
    type: "success" | "error" | "info";
}

type Listener = (toasts: ToastItem[]) => void;

let _toasts: ToastItem[] = [];
let _nextId = 1;
const _listeners: Set<Listener> = new Set();

function notify() {
    _listeners.forEach((fn) => fn([..._toasts]));
}

function push(message: string, type: ToastItem["type"], duration = 3500) {
    const id = _nextId++;
    _toasts = [..._toasts, { id, message, type }];
    notify();
    setTimeout(() => {
        _toasts = _toasts.filter((t) => t.id !== id);
        notify();
    }, duration);
}

/** API singleton para llamar desde cualquier lugar sin hooks */
export const toast = {
    success: (msg: string, ms?: number) => push(msg, "success", ms),
    error: (msg: string, ms?: number) => push(msg, "error", ms),
    info: (msg: string, ms?: number) => push(msg, "info", ms),
};

/** Hook para que <Toaster /> se suscriba a los cambios */
export function useToastStore() {
    const [items, setItems] = useState<ToastItem[]>([]);

    const listener = useCallback((next: ToastItem[]) => setItems(next), []);

    useEffect(() => {
        _listeners.add(listener);
        return () => { _listeners.delete(listener); };
    }, [listener]);

    return items;
}
